import { ActivityIndicator } from 'react-native-paper';
import React from 'react';
import { TextStyle, View } from 'react-native';

import { Button } from '@components/Button';
import { ToastType } from '@providers/Toast';
import { Typography } from '@components/Typography';
import { Icon, IconName } from '@components/Icon';
import { StyleObj, Theme, makeStyles, useTheme } from '@providers/Theme';

type ToastProps = {
  type: ToastType;
  title: React.ReactNode;
  body: React.ReactNode;
  isDismissible?: boolean;
  onClosePress?: () => void;
  style?: ToastStyleProp;
};
type ToastStyleProp = {
  toast: StyleObj;
  container: StyleObj;
};

type PresetDef = (theme: Theme) => StyleObj;

const typeIconName: Record<ToastType, IconName> = {
  info: 'info-circle',
  error: 'info-circle',
  loading: 'alarm',
  success: 'check-circle',
  warning: 'alert-triangle',
};

const presets: Record<ToastType, PresetDef> = {
  loading: (theme) => ({
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.cool[700],
  }),
  info: (theme) => ({
    borderColor: theme.palette.grey[400],
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.cool[700],
  }),
  error: (theme) => ({
    borderColor: theme.palette.error[200],
    backgroundColor: theme.palette.error[50],
    color: theme.palette.error[300],
  }),
  warning: (theme) => ({
    borderColor: theme.palette.warning[300],
    backgroundColor: theme.palette.warning[100],
    color: theme.palette.warning[700],
  }),
  success: (theme) => ({
    borderColor: theme.palette.success[100],
    backgroundColor: theme.palette.success[50],
    color: theme.palette.success[500],
  }),
};

const useStyles = makeStyles<Pick<ToastProps, 'type'>>((theme: Theme) => ({
  wrapper: {
    width: '100%',
    alignItems: 'flex-end',
    ...theme.mixins.padding(0, 2),
  },
  root: (props) => ({
    width: '100%',
    display: 'flex',
    borderWidth: 1,
    borderRadius: 8,
    ...theme.mixins.padding(2),
    ...presets[props.type](theme),
    flexDirection: 'column',
    on: {
      sm: {
        flexDirection: 'row',
        width: 600,
        maxWidth: '100%',
      },
    },
  }),
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    // alignItems: 'center',
    on: {
      sm: {
        justifyContent: 'flex-start',
        // alignItems: 'flex-start',
        marginRight: theme.spacing(1),
      },
    },
  },
  icon: {
    transform: [{ rotateY: '0deg' }],
  },
  content: {
    flex: 1,
  },
  title: {
    marginTop: theme.spacing(1.5),
    on: {
      sm: {
        marginTop: theme.spacing(0),
      },
    },
  },
  body: {
    marginTop: theme.spacing(0.5),
  },
  closeButton: {
    marginTop: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    on: {
      sm: {
        display: 'none',
      },
    },
  },
  desktopCloseButton: {
    marginTop: theme.spacing(-1),
    marginRight: theme.spacing(-1),
    display: 'none',
    on: {
      sm: {
        display: undefined,
      },
    },
  },
}));

export const Toast: React.FC<ToastProps> = ({
  type,
  title,
  body,
  isDismissible,
  onClosePress,
  style,
}) => {
  const theme = useTheme();
  const preset = presets[type](theme);
  const styles = useStyles({ type });

  const color = (preset as TextStyle).color as string;

  return (
    <View style={[styles.wrapper, style?.toast]}>
      <View style={[styles.root, style?.container]}>
        <View style={styles.header}>
          {type === 'loading' ? (
            <ActivityIndicator color={color} size={17} />
          ) : (
            <Icon name={typeIconName[type]} color={color} style={styles.icon} />
          )}

          {isDismissible && (
            <Button
              style={styles.closeButton}
              onPress={onClosePress}
              startIcon={<Icon name="close" color={color} />}
            />
          )}
        </View>
        <View style={styles.content}>
          <Typography weight="semiBold" size="sm" style={styles.title}>
            {title}
          </Typography>
          <Typography weight="regular" size="sm" style={styles.body}>
            {body}
          </Typography>
        </View>
        {isDismissible && (
          <View>
            <Button
              style={styles.desktopCloseButton}
              onPress={onClosePress}
              startIcon={<Icon name="close" color={color} />}
            />
          </View>
        )}
      </View>
    </View>
  );
};
